"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { PillToggle } from "@/components/ui/controls/PillToggle";
import type { Stats } from "../types";
import { useLoyaltyConfig } from "../hooks/useLoyaltyConfig";
import HeroBanner from "./activity/HeroBanner";
import StatsGrid from "./activity/StatsGrid";
import BadgesShowcase from "./activity/BadgesShowcase";
import PredictionStats from "./activity/PredictionStats";
import PredictionHistory from "./activity/PredictionHistory";
import DiscussionStats from "./activity/DiscussionStats";
import GamesVotesHistory from "./activity/GamesVotesHistory";
type Section = "overview" | "predictions" | "games" | "badges" | "discussions";
interface ActivityTabProps {
  stats: Stats;
  labels: Record<string, string>;
}
export default function ActivityTab(t0) {
  const $ = _c(66);
  const {
    stats,
    labels
  } = t0;
  const [activeSection, setActiveSection] = useState("overview");
  const {
    tierConfigs,
    badgeDefinitions,
    isLoading: loyaltyLoading
  } = useLoyaltyConfig();
  let t1;
  if ($[0] !== badgeDefinitions) {
    t1 = Object.keys(badgeDefinitions);
    $[0] = badgeDefinitions;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasBadges = t1.length > 0;
  const hasDiscussions = stats.discussions && stats.discussions.totalPoints > 0;
  const t2 = labels.sectionOverview || "Overview";
  let t3;
  if ($[2] !== t2) {
    t3 = {
      id: "overview",
      label: t2
    };
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = labels.sectionPredictions || "Predictions";
  let t5;
  if ($[4] !== t4) {
    t5 = {
      id: "predictions",
      label: t4
    };
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = labels.sectionGames || "Games & Votes";
  let t7;
  if ($[6] !== t6) {
    t7 = {
      id: "games",
      label: t6
    };
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let sections;
  if ($[8] !== hasBadges || $[9] !== hasDiscussions || $[10] !== labels.sectionBadges || $[11] !== labels.sectionDiscussions || $[12] !== loyaltyLoading || $[13] !== t3 || $[14] !== t5 || $[15] !== t7) {
    sections = [t3, t5, t7];
    if (!loyaltyLoading && hasBadges) {
      const t8 = labels.sectionBadges || "Badges";
      let t9;
      if ($[17] !== t8) {
        t9 = {
          id: "badges",
          label: t8
        };
        $[17] = t8;
        $[18] = t9;
      } else {
        t9 = $[18];
      }
      sections.push(t9);
    }
    if (hasDiscussions) {
      const t8 = labels.sectionDiscussions || "Discussions";
      let t9;
      if ($[19] !== t8) {
        t9 = {
          id: "discussions",
          label: t8
        };
        $[19] = t8;
        $[20] = t9;
      } else {
        t9 = $[20];
      }
      sections.push(t9);
    }
    $[8] = hasBadges;
    $[9] = hasDiscussions;
    $[10] = labels.sectionBadges;
    $[11] = labels.sectionDiscussions;
    $[12] = loyaltyLoading;
    $[13] = t3;
    $[14] = t5;
    $[15] = t7;
    $[16] = sections;
  } else {
    sections = $[16];
  }
  let t8;
  if ($[21] !== stats.successRates?.byFootballMarket) {
    t8 = Object.entries(stats.successRates?.byFootballMarket || {}).map(_temp);
    $[21] = stats.successRates?.byFootballMarket;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  const byMarket = t8;
  let t9;
  if ($[23] !== activeSection || $[24] !== sections) {
    t9 = <PillToggle options={sections} activeId={activeSection} onChange={setActiveSection} size="md" />;
    $[23] = activeSection;
    $[24] = sections;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== activeSection || $[27] !== labels || $[28] !== loyaltyLoading || $[29] !== stats.badges?.length || $[30] !== stats.points || $[31] !== stats.predictionsMade || $[32] !== stats.successRates?.overallPercent || $[33] !== stats.tier || $[34] !== tierConfigs) {
    t10 = activeSection === "overview" && <div className="flex flex-col gap-8">{loyaltyLoading ? <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : <HeroBanner points={stats.points} currentTier={stats.tier} tierConfigs={tierConfigs} labels={labels} />}<StatsGrid predictionsMade={stats.predictionsMade} winRate={stats.successRates?.overallPercent ?? 0} totalPoints={stats.points} badgesEarned={stats.badges?.length ?? 0} labels={labels} /></div>;
    $[26] = activeSection;
    $[27] = labels;
    $[28] = loyaltyLoading;
    $[29] = stats.badges?.length;
    $[30] = stats.points;
    $[31] = stats.predictionsMade;
    $[32] = stats.successRates?.overallPercent;
    $[33] = stats.tier;
    $[34] = tierConfigs;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== activeSection || $[37] !== byMarket || $[38] !== labels || $[39] !== stats.predictions?.matchQuiz || $[40] !== stats.predictions?.single || $[41] !== stats.predictions?.topX || $[42] !== stats.predictionsMade || $[43] !== stats.successRates?.overallPercent) {
    t11 = activeSection === "predictions" && <div className="flex flex-col gap-8">{stats.predictionsMade > 0 && <PredictionStats overallSuccessRate={stats.successRates?.overallPercent ?? 0} byMarket={byMarket} single={stats.predictions?.single || {
        predictionsMade: 0,
        correct: 0,
        points: 0
      }} topX={stats.predictions?.topX || {
        participations: 0,
        points: 0
      }} matchQuiz={stats.predictions?.matchQuiz || {
        participations: 0,
        points: 0
      }} labels={labels} />}<PredictionHistory labels={labels} /></div>;
    $[36] = activeSection;
    $[37] = byMarket;
    $[38] = labels;
    $[39] = stats.predictions?.matchQuiz;
    $[40] = stats.predictions?.single;
    $[41] = stats.predictions?.topX;
    $[42] = stats.predictionsMade;
    $[43] = stats.successRates?.overallPercent;
    $[44] = t11;
  } else {
    t11 = $[44];
  }
  let t12;
  if ($[45] !== activeSection || $[46] !== labels) {
    t12 = activeSection === "games" && <GamesVotesHistory labels={labels} />;
    $[45] = activeSection;
    $[46] = labels;
    $[47] = t12;
  } else {
    t12 = $[47];
  }
  let t13;
  if ($[48] !== activeSection || $[49] !== badgeDefinitions || $[50] !== hasBadges || $[51] !== labels || $[52] !== stats.badges) {
    t13 = activeSection === "badges" && hasBadges && <BadgesShowcase badgeDefinitions={badgeDefinitions} earnedBadges={stats.badges || []} labels={labels} />;
    $[48] = activeSection;
    $[49] = badgeDefinitions;
    $[50] = hasBadges;
    $[51] = labels;
    $[52] = stats.badges;
    $[53] = t13;
  } else {
    t13 = $[53];
  }
  let t14;
  if ($[54] !== activeSection || $[55] !== hasDiscussions || $[56] !== labels || $[57] !== stats.discussions) {
    t14 = activeSection === "discussions" && hasDiscussions && <DiscussionStats postsMade={stats.discussions.postsMade} reactionCount={stats.discussions.reactionCount} totalPoints={stats.discussions.totalPoints} labels={labels} />;
    $[54] = activeSection;
    $[55] = hasDiscussions;
    $[56] = labels;
    $[57] = stats.discussions;
    $[58] = t14;
  } else {
    t14 = $[58];
  }
  let t15;
  if ($[59] !== t10 || $[60] !== t11 || $[61] !== t12 || $[62] !== t13 || $[63] !== t14 || $[64] !== t9) {
    t15 = <div className="flex flex-col gap-6">{t9}{t10}{t11}{t12}{t13}{t14}</div>;
    $[59] = t10;
    $[60] = t11;
    $[61] = t12;
    $[62] = t13;
    $[63] = t14;
    $[64] = t9;
    $[65] = t15;
  } else {
    t15 = $[65];
  }
  return t15;
}
function _temp(t0) {
  const [market, data] = t0;
  return {
    market,
    successRatePercent: data.successRatePercent
  };
}